import {
	MarketBasket,
	MarketBasketExclusion,
	MarketBasketKpis,
	MarketBasketSettings,
	MarketBasketSummary,
	Purchase,
	PurchaseCounts,
	PurchaseGroupDetails,
	PurchaseTotals,
	SimilarProduct,
} from '@domain'



import api from '.';
import { BlanketMarkupActionReq, CreateMarketBasketExcelExportReq, CreateMarketBasketPdfExportReq, CreateMarketBasketReq, MarketBasketActionReq, MarketBasketCloneReq, MarketBasketExportRes, MarketBasketSalesforceExclusionReq, MarketBasketSimilarProductsReq, MarketBasketsReq, MatchFeedbackReq, PurchaseFilters, UnmatchFeedbackReq, UpdateMarketBasketExclusionReq, UpdateMarketBasketReq } from './marketBaskets.types';
import { SimpleListItemRes, StringListRes } from './shared.types';


// Query Key factory
export const MarketBasketsKeys = {
	all: ['market-baskets'],
	list: (query: MarketBasketsReq) => [...MarketBasketsKeys.all, query],
	marketBasket: (id: Id) => ['market-basket', id],
	exports: ({ id, exportId }: { id: Id; exportId: Id }) => [
		...MarketBasketsKeys.marketBasket(id),
		'exports',
		exportId,
	],
	purchases: (id: Id, query: PurchaseFilters & PaginationParams) => [
		...MarketBasketsKeys.marketBasket(id),
		'purchases',
		query,
	],
	purchaseTotals: (id: Id, query: PurchaseFilters & PaginationParams) => [
		...MarketBasketsKeys.marketBasket(id),
		'purchases',
		'totals',
		query,
	],
	purchaseCounts: (id: Id, query: PurchaseFilters) => [
		...MarketBasketsKeys.marketBasket(id),
		'purchases',
		'counts',
		query,
	],
	purchase: ({ id, purchaseId }: { id: Id; purchaseId: Id }) => [
		...MarketBasketsKeys.marketBasket(id),
		'purchase',
		{ groupKey: purchaseId },
	],
	invoiceLineIds: ({ id, purchaseId }: { id: Id; purchaseId: Id }) => [
		...MarketBasketsKeys.marketBasket(id),
		'purchases',
		'invoice-line-ids',
		{ groupKey: purchaseId },
	],
	kpis: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'kpis'],
	similarProduct: ({ marketBasketId, productId }: { marketBasketId: Id; productId: Id }) => [
		...MarketBasketsKeys.marketBasket(marketBasketId),
		'purchase',
		{ productId },
	],
	similarProducts: ({
		marketBasketId,
		purchaseId,
		meetsCriteria,
	}: MarketBasketSimilarProductsReq) => [
		...MarketBasketsKeys.marketBasket(marketBasketId),
		'similar-products',
		{ purchaseId, meetsCriteria },
	],
	purchaseManufacturers: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'manufacturers', id],
	settings: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'settings'],
	manufacturers: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'manufacturers'],
	customers: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'customers'],
	packSizes: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'packSizes'],
	categories: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'categories'],
	groups: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'groups'],
	manualExclusions: (id: Id) => [...MarketBasketsKeys.marketBasket(id), 'manual-exclusions'],
	salesforceExclusions: (id: Id, query: MarketBasketSalesforceExclusionReq) => [
		...MarketBasketsKeys.marketBasket(id),
		'salesforce-exclusions',
		query,
	],
	salesforceExclusionsCustomers: (id: Id) => [
		...MarketBasketsKeys.marketBasket(id),
		'salesforce-exclusions',
		'customers',
	],
	salesforceExclusionsManufacturers: (id: Id) => [
		...MarketBasketsKeys.marketBasket(id),
		'salesforce-exclusions',
		'manufacturers',
	],
	salesforceExclusionsSources: (id: Id) => [
		...MarketBasketsKeys.marketBasket(id),
		'salesforce-exclusions',
		'sources',
	],
}

const MarketBasketsApi = {
	// =================================
	// GET
	// =================================
	list: (query: MarketBasketsReq) =>
		api
			.get<PaginatedAPIRes<MarketBasketSummary[]>>(`/market-baskets`, { params: query })
			.then((res) => res.data),
	marketBasket: (id: string) =>
		api.get<APIRes<MarketBasket>>(`/market-baskets/${id}`).then((res) => res.data.data),
	exports: ({ id, exportId }: { id: Id; exportId: Id }) =>
		api
			.get<MarketBasketExportRes>(`/market-baskets/${id}/exports/${exportId}`)
			.then((res) => res.data.data),
	purchases: (id: Id, query: PurchaseFilters & PaginationParams) =>
		api
			.get<PaginatedAPIRes<Purchase[]>>(`/market-baskets/${id}/purchases`, { params: query })
			.then((res) => res.data),
	purchaseTotals: (id: Id, query: PurchaseFilters & PaginationParams) =>
		api
			.get<APIRes<PurchaseTotals>>(`/market-baskets/${id}/purchases/totals`, { params: query })
			.then((res) => res.data.data),
	purchaseCounts: (id: Id, req: PurchaseFilters) =>
		api
			.get<APIRes<PurchaseCounts>>(`/market-baskets/${id}/purchases/counts`, { params: req })
			.then((res) => res.data.data),
	kpis: (id: Id) =>
		api.get<APIRes<MarketBasketKpis>>(`/market-baskets/${id}/kpis`).then((res) => res.data.data),
	purchase: ({ id, purchaseId }: { id: Id; purchaseId: Id }) =>
		api
			.get<APIRes<PurchaseGroupDetails>>(`/market-baskets/${id}/purchases/group`, {
				params: { groupKey: purchaseId },
			})
			.then((res) => res.data),
	invoiceLineIds: ({ id, purchaseId }: { id: Id; purchaseId: Id }) =>
		api
			.get<APIRes<Id[]>>(`/market-baskets/${id}/purchases/invoice-line-ids`, {
				params: { groupKey: purchaseId },
			})
			.then((res) => res.data.data),
	settings: (id: Id) =>
		api
			.get<APIRes<MarketBasketSettings>>(`/market-baskets/${id}/settings`)
			.then((res) => res.data.data),
	similarProduct: ({ marketBasketId, productId }: { marketBasketId: Id; productId: Id }) =>
		api
			.get<
				APIRes<SimilarProduct>
			>(`/market-baskets/${marketBasketId}/similar-products/${productId}`)
			.then((res) => res.data.data),
	similarProducts: ({
		marketBasketId,
		purchaseId,
		meetsCriteria,
	}: MarketBasketSimilarProductsReq) =>
		api
			.get<APIRes<SimilarProduct[]>>(`/market-baskets/${marketBasketId}/similar-products`, {
				params: { groupKey: purchaseId, meetsCriteria },
			})
			.then((res) => res.data.data),
	manufacturers: (id: Id) =>
		api.get<SimpleListItemRes>(`/market-baskets/${id}/manufacturers`).then((res) => res.data.data),
	customers: (id: Id) =>
		api.get<SimpleListItemRes>(`/market-baskets/${id}/customers`).then((res) => res.data.data),
	packSizes: (id: Id) =>
		api.get<StringListRes>(`/market-baskets/${id}/pack-sizes`).then((res) => res.data.data),
	categories: (id: Id) =>
		api.get<SimpleListItemRes>(`/market-baskets/${id}/categories`).then((res) => res.data.data),
	groups: (id: Id) =>
		api
			.get<SimpleListItemRes>(`/market-baskets/${id}/purchase-group-keys`)
			.then((res) => res.data.data),
	manualExclusions: (id: Id) =>
		api
			.get<APIRes<MarketBasketExclusion[]>>(`/market-baskets/${id}/exclusions/manual`)
			.then((res) => res.data.data),
	salesforceExclusions: (id: Id, query: MarketBasketSalesforceExclusionReq) =>
		api
			.get<
				PaginatedAPIRes<MarketBasketExclusion[]>
			>(`/market-baskets/${id}/exclusions/salesforce`, { params: query })
			.then((res) => res.data),
	salesforceExclusionsCustomers: (id: Id) =>
		api
			.get<SimpleListItemRes>(`/market-baskets/${id}/exclusions/salesforce/customers`)
			.then((res) => res.data.data),
	salesforceExclusionsManufacturers: (id: Id) =>
		api
			.get<SimpleListItemRes>(`/market-baskets/${id}/exclusions/salesforce/manufacturers`)
			.then((res) => res.data.data),
	salesforceExclusionsSources: (id: Id) =>
		api
			.get<SimpleListItemRes>(`/market-baskets/${id}/exclusions/salesforce/sources`)
			.then((res) => res.data.data),

	// =================================
	// POST
	// =================================
	add: ({ marketBasket, files }: CreateMarketBasketReq) => {
		if (files) {
			const formData = new FormData()
			// Append newMarketBasket as a JSON string
			formData.append(
				'request',
				new Blob([JSON.stringify(marketBasket)], { type: 'application/json' })
			)
			// Append each file separately
			files.forEach((file) => formData.append('files', file))
			// postForm method overwrites content-type header to multipart/form-data
			return api.postForm('/market-baskets', formData)
		}
		return api.post('/market-baskets', marketBasket)
	},
	restore: (id: number) => api.post(`/market-baskets/${id}/restore`),
	complete: (id: Id) => api.post(`/market-baskets/${id}/complete`),
	skipTasks: (id: Id) => api.post(`/market-baskets/${id}/skip-tasks`),
	export: ({ id, settings }: CreateMarketBasketExcelExportReq) =>
		api.post(`/market-baskets/${id}/export`, settings).then((res) => res.data.data),
	exportPdf: ({ id, settings }: CreateMarketBasketPdfExportReq) =>
		api
			.post<Blob>(`/market-baskets/${id}/export-pdf`, settings, { responseType: 'blob' })
			.then((res) => res.data),
	clone: ({ id, cloneFields }: MarketBasketCloneReq) =>
		api.post(`/market-baskets/${id}/clone`, cloneFields),
	markupAddon: ({
		id,
		purchaseIds,
		filter,
		addon,
		description,
	}: MarketBasketActionReq & {
		addon: number
		description: string
	}) =>
		api.post(`/market-baskets/${id}/markup-addon`, {
			groupKeys: purchaseIds,
			filter,
			addon,
			description,
		}),
	sellPrice: ({
		id,
		purchaseIds,
		filter,
		sellPrice,
	}: MarketBasketActionReq & {
		sellPrice: number
	}) =>
		api.post(`/market-baskets/${id}/sell-price`, {
			groupKeys: purchaseIds,
			filter,
			sellPrice,
		}),
	updateBlanketMarkup: ({ id, markupType, markupAmount }: BlanketMarkupActionReq) =>
		api.post(`/market-baskets/${id}/blanket-markup`, { markupType, markupAmount }),
	packSize: ({
		id,
		purchaseIds,
		filter,
		packPurchaseQty,
		packSimilarQty,
	}: MarketBasketActionReq & {
		packPurchaseQty: number
		packSimilarQty: number
	}) =>
		api.post(`/market-baskets/${id}/pack-size`, {
			groupKeys: purchaseIds,
			filter,
			packPurchaseQty,
			packSimilarQty,
		}),
	unmatch: ({ id, purchaseIds, filter }: MarketBasketActionReq) =>
		api.post(`/market-baskets/${id}/un-match`, { groupKeys: purchaseIds, filter }),
	unmatchFeedback: ({ id, groupKeys, filter, observation }: UnmatchFeedbackReq) =>
		api.post(`/market-baskets/${id}/similar-products/unmatch-feedback`, {
			groupKeys,
			filter,
			observation,
		}),
	match: ({
		id,
		purchaseIds,
		similarProductId,
	}: MarketBasketActionReq & {
		similarProductId: Id
	}) => api.post(`/market-baskets/${id}/match`, { groupKeys: purchaseIds, similarProductId }),
	matchFeedback: ({ id, overrideMatchPlid, mismatchReason, groupKey }: MatchFeedbackReq) =>
		api.post(`/market-baskets/${id}/similar-products/switch-feedback`, {
			overrideMatchPlid,
			mismatchReason,
			groupKey,
		}),
	addToBasket: ({ id, purchaseIds, filter }: MarketBasketActionReq) =>
		api.post(`/market-baskets/${id}/basket-add`, {
			groupKeys: purchaseIds,
			filter,
		}),
	removeFromBasket: ({ id, purchaseIds, filter }: MarketBasketActionReq) =>
		api.post(`/market-baskets/${id}/basket-remove`, {
			groupKeys: purchaseIds,
			filter,
		}),

	// =================================
	// PATCH
	// =================================
	update: ({ id, marketBasket, files }: UpdateMarketBasketReq) => {
		if (files) {
			const formData = new FormData()
			// Append newMarketBasket as a JSON string
			formData.append(
				'request',
				new Blob([JSON.stringify(marketBasket)], { type: 'application/json' })
			)
			// Append each file separately
			files.forEach((file) => formData.append('files', file))
			// patchForm method overwrites content-type header to multipart/form-data
			return api.patchForm(`/market-baskets/${id}`, formData)
		}
		return api.patch(`/market-baskets/${id}`, marketBasket)
	},
	updateSettings: ({ id, settings }: { id: Id; settings: MarketBasketSettings }) =>
		api.patch(`/market-baskets/${id}/settings`, settings, { timeout: 30000 }),
	updateExclusions: ({ id, exclusions }: UpdateMarketBasketExclusionReq) =>
		api.patch(`/market-baskets/${id}/exclusions`, exclusions),
	updateGroupBy: ({ id, groupBy }: { id: Id; groupBy: string }) =>
		api.patch(`/market-baskets/${id}/group-by`, { groupBy }),

	// DELETE
	delete: (id: number) => api.delete(`/market-baskets/${id}`),
	removeBlanketMarkup: (id: Id) => api.delete(`/market-baskets/${id}/blanket-markup`),
}

export default MarketBasketsApi
